import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Experiment {
  id: string
  name: string
  status: string
  start_date: string
  end_date: string | null
  control_conversion_rate: number
  variant_conversion_rate: number
  lift: number
  confidence: number
  sample_size: number
  statistical_significance: boolean
}

interface Variant {
  id: string
  experiment_id: string
  name: string
  traffic_percent: number
  conversions: number
  visitors: number
  conversion_rate: number
  revenue: number
  is_winner: boolean
}

interface Statistics {
  total_experiments: number
  active_experiments: number
  completed_experiments: number
  avg_lift: number
  avg_confidence: number
  total_visitors_tested: number
  winning_experiments: number
  total_revenue_impact: number
}

export default function ABTestingAnalyticsPage() {
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [variants, setVariants] = useState<Variant[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'experiments' | 'variants'>('experiments')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [expRes, varRes, statsRes] = await Promise.all([
        fetch('/api/abtestinganalytics/experiments'),
        fetch('/api/abtestinganalytics/variants'),
        fetch('/api/abtestinganalytics/statistics')
      ])

      if (expRes.ok) setExperiments(await expRes.json())
      if (varRes.ok) setVariants(await varRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching A/B testing analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      running: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      draft: 'bg-gray-100 text-gray-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">A/B Testing Analytics</h1>
          <p className="mt-2 text-gray-600">Experiment performance and statistical analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Experiments</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_experiments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Lift</p>
              <p className="text-2xl font-bold text-blue-600">{(statistics.avg_lift * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Winning Tests</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.winning_experiments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue Impact</p>
              <p className="text-2xl font-bold text-green-600">${statistics.total_revenue_impact.toLocaleString()}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('experiments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'experiments' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Experiments ({experiments.length})
            </button>
            <button
              onClick={() => setActiveTab('variants')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'variants' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Variants ({variants.length})
            </button>
          </nav>
        </div>

        {activeTab === 'experiments' && (
          <div className="space-y-4">
            {experiments.map((exp) => (
              <div key={exp.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{exp.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(exp.status)}`}>
                        {exp.status}
                      </span>
                      {exp.statistical_significance && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Significant
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${exp.lift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {exp.lift >= 0 ? '+' : ''}{(exp.lift * 100).toFixed(1)}%
                    </p>
                    <p className="text-sm text-gray-500">lift</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Control Rate</p>
                    <p className="text-lg font-bold text-gray-900">{(exp.control_conversion_rate * 100).toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Variant Rate</p>
                    <p className="text-lg font-bold text-blue-600">{(exp.variant_conversion_rate * 100).toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Confidence</p>
                    <p className="text-lg font-bold text-purple-600">{(exp.confidence * 100).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sample Size</p>
                    <p className="text-lg font-bold text-gray-900">{exp.sample_size.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'variants' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traffic</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitors</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv. Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Winner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {variants.map((variant) => (
                  <tr key={variant.id} className={variant.is_winner ? 'bg-green-50' : ''}>
                    <td className="px-6 py-4 font-medium text-gray-900">{variant.name}</td>
                    <td className="px-6 py-4 text-gray-500">{(variant.traffic_percent * 100).toFixed(0)}%</td>
                    <td className="px-6 py-4 text-gray-500">{variant.visitors.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gray-500">{variant.conversions.toLocaleString()}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{(variant.conversion_rate * 100).toFixed(2)}%</td>
                    <td className="px-6 py-4 text-green-600 font-medium">${variant.revenue.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      {variant.is_winner && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Winner</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
